import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    email: state.components.contactUsForm.fieldValues["email"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface EmailAddressStyles {
    emailTextField?: TextFieldPresentationProps;
}

export const emailStyles: EmailAddressStyles = {};

const styles = emailStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const EmailAddress: React.FC<Props> = ({ fields, email, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [emailErrorMessage, setEmailAddressErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.email = validateEmailAddress;
    }, [email]);

    React.useEffect(() => {
        contactFormContext.validators.email = validateEmailAddress;
    }, [isRequired]);

    const validateEmailAddress = () => {
        const errorMessage =
            !email && isRequired
                ? siteMessage("ContactUsForm_EmailIsRequiredErrorMessage")
                : !email || emailRegexp.test(email)
                ? ""
                : siteMessage("ContactUsForm_EmailIsInvalidErrorMessage");
        setEmailAddressErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "email", value: event.target.value });
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateEmailAddress();
    };

    return (
        <TextField
            {...styles.emailTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={email || ""}
            onChange={emailChangeHandler}
            onBlur={emailBlurHandler}
            error={emailErrorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EmailAddress),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Email Address",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter your email address",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
